import { Opportunity, LoanStage } from '../types/opportunity'

export type BannerStatus = 'error' | 'warning' | 'info' | 'success'

export interface BannerState {
  status: BannerStatus
  title: string
  message: string
  actionLabel: string
  actionType: 'primary' | 'secondary'
}

export const REQUIRED_FIELDS: Record<LoanStage, (keyof Opportunity)[]> = {
  application: ['borrowerName', 'loanAmount', 'propertyAddress'],
  underwriting: [
    'borrowerName',
    'loanAmount',
    'propertyAddress',
    'creditScore',
    'employmentVerified',
    'incomeDocuments',
  ],
  approval: [
    'borrowerName',
    'loanAmount',
    'propertyAddress',
    'creditScore',
    'employmentVerified',
    'incomeDocuments',
  ],
  closing: [
    'borrowerName',
    'loanAmount',
    'propertyAddress',
    'creditScore',
    'employmentVerified',
    'incomeDocuments',
    'appraisalOrdered',
    'titleSearch',
  ],
  funded: [
    'borrowerName',
    'loanAmount',
    'propertyAddress',
    'creditScore',
    'employmentVerified',
    'incomeDocuments',
    'appraisalOrdered',
    'titleSearch',
  ],
}

export const FIELD_LABELS: Partial<Record<keyof Opportunity, string>> = {
  borrowerName: 'Borrower Name',
  loanAmount: 'Loan Amount',
  propertyAddress: 'Property Address',
  creditScore: 'Credit Score',
  employmentVerified: 'Employment Verification',
  incomeDocuments: 'Income Documents',
  appraisalOrdered: 'Appraisal Order',
  titleSearch: 'Title Search',
}

export function useOpportunityStatus(opportunity: Opportunity): BannerState {
  // 1. Check for critical open cases first
  const criticalCases = opportunity.cases.filter(
    (c) => c.status === 'open' && c.severity === 'critical',
  )
  if (criticalCases.length > 0) {
    const caseList = criticalCases.map((c) => c.title).join(', ')
    return {
      status: 'error',
      title: 'Urgent: Resolve Critical Cases',
      message: `You need to address these cases before moving forward: ${caseList}`,
      actionLabel: 'View Cases',
      actionType: 'primary',
    }
  }

  // 2. Check for missing required fields
  const requiredFields = REQUIRED_FIELDS[opportunity.stage]
  const missingFields = requiredFields.filter((field) => {
    const value = opportunity[field]
    return (
      value === undefined || value === null || value === false || value === ''
    )
  })

  if (missingFields.length > 0) {
    const fieldList = missingFields.map((f) => FIELD_LABELS[f] || f).join(', ')
    return {
      status: 'warning',
      title: 'Next Step: Add Missing Information',
      message: `You need to add: ${fieldList}`,
      actionLabel: 'Add Missing Info',
      actionType: 'primary',
    }
  }

  // 3. All requirements met
  return {
    status: 'success',
    title: 'All Steps Satisfied',
    message:
      'All required information has been provided for the current stage.',
    actionLabel: 'View Details',
    actionType: 'secondary',
  }
}
